import { Extension, Editor } from '@tiptap/core';
import { Plugin, PluginKey, TextSelection } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { AIServiceFactory } from '../../services/ai/AIServiceFactory';
import userSettings from '../../utils/UserSettings';
import { Transaction } from '@tiptap/pm/state';
import { MarkdownProcessorPluginImpl } from '../plugins/markdown-processor';

// 创建Markdown处理器实例
const markdownProcessor = new MarkdownProcessorPluginImpl();

// 思考状态装饰器的插件键
const aiThinkingPluginKey = new PluginKey('aiThinking');

// 声明命令类型
declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    aiText: {
      /**
       * 生成AI文本
       */
      generateText: () => ReturnType,
      /**
       * 确认AI文本
       */
      confirmAIText: () => ReturnType,
      /**
       * 取消AI文本
       */
      cancelAIText: () => ReturnType,
    }
  }
}

// AI生成状态枚举
enum AIGenerationStatus {
  Idle = 'idle',               // 空闲状态
  Started = 'started',         // 开始请求
  Thinking = 'thinking',       // 思考中
  Generating = 'generating',   // 生成内容中
  Error = 'error',             // 错误状态
  Completed = 'completed'      // 完成状态
}

// AI 思考和生成内容的接口
interface AIGenerationState {
  status: AIGenerationStatus;  // 生成状态
  thinking: boolean;           // 兼容旧逻辑，后续可移除
  content: string;             // 保存完整内容用于确认时使用
  startPos: number;            // 插入开始位置
  endPos: number;              // 当前内容的结束位置
  endMarkerPos: number | null;
  showAIPrefix: boolean;       // 是否显示AI前缀
  tempContent: string;         // 临时内容，用于装饰器展示
  errorMessage?: string;       // 错误信息
  thinkingContent?: string;    // 思考内容
}

// AI响应元数据接口
interface AiResponseMeta {
  content: string;
  isThinking: boolean;
  updateType?: 'init' | 'status' | 'thinking' | 'content' | 'position';
}

// 扩展存储类型
interface AITextStorage {
  isGenerating: boolean;
  abortController: AbortController | null;
  generationState: AIGenerationState | null;
  isAIUpdating: boolean;
  isInThinkingPhase: boolean;
  accumulatedText: string;
  mdProcessor: typeof markdownProcessor;
}

// 全局方法，用于更新AI生成文本
const handleTextUpdate = async (self: { storage: AITextStorage }, editor: Editor, token: string): Promise<void> => {
  if (!self.storage.isGenerating || !self.storage.generationState) return;

  const state = self.storage.generationState;
  
  // 将新token添加到累积文本中
  self.storage.accumulatedText += token;
  const text = self.storage.accumulatedText;
  
  // 检查是否包含思考标记
  if (text.includes('<think>') && !text.includes('</think>')) {
    // 在思考阶段，但没有结束标记
    if (!self.storage.isInThinkingPhase) {
      // 刚开始思考，标记状态
      self.storage.isInThinkingPhase = true;
      state.thinking = true;
      state.status = AIGenerationStatus.Thinking;
      
      // 提取当前的思考内容
      const thinkPattern = /<think>([\s\S]*)$/;
      const match = text.match(thinkPattern);
      if (match) {
        state.thinkingContent = match[1] || '';
      }
      
      // 触发编辑器重新渲染，更新状态
      if (editor.view) {
        // 首先更新状态
        const statusTr = editor.state.tr;
        editor.view.dispatch(statusTr.setMeta(aiThinkingPluginKey, { 
          generationState: state,
          updateType: 'status' // 明确指定只更新状态
        }));
        
        // 然后初始显示思考内容
        const thinkingTr = editor.state.tr;
        editor.view.dispatch(thinkingTr.setMeta(aiThinkingPluginKey, { 
          generationState: state,
          updateType: 'thinking' // 显示思考内容
        }));
      }
    } else {
      // 已在思考阶段，持续接收新token
      // 提取当前完整的思考内容
      const thinkPattern = /<think>([\s\S]*)$/;
      const match = text.match(thinkPattern);
      if (match) {
        state.thinkingContent = match[1] || '';
        
        // 实时更新思考内容显示
        if (editor.view) {
          const thinkingTr = editor.state.tr;
          editor.view.dispatch(thinkingTr.setMeta(aiThinkingPluginKey, { 
            generationState: state,
            updateType: 'thinking' // 更新思考内容
          }));
        }
      }
    }
  } else if (text.includes('</think>') && self.storage.isInThinkingPhase) {
    // 思考阶段结束
    self.storage.isInThinkingPhase = false;
    state.thinking = false;
    state.status = AIGenerationStatus.Generating;
    
    // 提取思考内容和正文内容
    const fullPattern = /<think>([\s\S]*?)<\/think>([\s\S]*)$/;
    const match = text.match(fullPattern);
    
    if (match) {
      // 提取思考内容并存储
      const thinkingContent = match[1] || '';
      state.thinkingContent = thinkingContent;
      
      // 提取初始正文
      const initialContent = match[2] || '';
      
      if (initialContent) {
        // 更新当前内容
        state.content = initialContent;
        state.tempContent = initialContent;
        
        // 重置增量处理器
        self.storage.mdProcessor.reset();
        
        // 转换为HTML并通过装饰器显示
        const htmlContent = self.storage.mdProcessor.contentToHtml(initialContent);
        
        // 更新装饰器以显示内容
        if (editor.view) {
          const tr = editor.state.tr;
          editor.view.dispatch(tr.setMeta(aiThinkingPluginKey, { 
            generationState: state,
            tempHtml: htmlContent,
            updateType: 'thinking' // 先更新思考内容
          }));
          
          // 单独更新状态和内容，以确保装饰器能正确处理
          setTimeout(() => {
            if (editor.view) {
              const contentTr = editor.state.tr;
              editor.view.dispatch(contentTr.setMeta(aiThinkingPluginKey, { 
                generationState: state,
                tempHtml: htmlContent,
                updateType: 'content' // 然后更新内容
              }));
            }
          }, 10);
        }
      }
    }
  } else if (!self.storage.isInThinkingPhase) {
    // 不在思考阶段，处理正常文本
    
    // 只有当有新内容时才更新
    if (token && editor.view) {
      // 如果状态仍为Started，更新为Generating
      let statusChanged = false;
      if (state.status === AIGenerationStatus.Started) {
        state.status = AIGenerationStatus.Generating;
        statusChanged = true;
      }
      
      // 更新总内容
      state.content += token;
      state.tempContent = state.content;
      
      // 获取完整HTML内容
      const htmlContent = self.storage.mdProcessor.contentToHtml(state.content);
      
      // 每个token都更新UI，移除缓冲条件
      // 如果状态有变更，先更新状态
      if (statusChanged) {
        const statusTr = editor.state.tr;
        editor.view.dispatch(statusTr.setMeta(aiThinkingPluginKey, { 
          generationState: state,
          tempHtml: '',
          updateType: 'status'
        }));
      }
      
      // 然后更新内容
      const contentTr = editor.state.tr;
      editor.view.dispatch(contentTr.setMeta(aiThinkingPluginKey, { 
        generationState: state,
        tempHtml: htmlContent,
        updateType: 'content' // 明确指定只更新内容
      }));
    }
  }
  
  // 检查生成是否已经完成（没有更多token）
  if (!token && !self.storage.isInThinkingPhase && state.status === AIGenerationStatus.Generating) {
    // 标记生成已完成，不再显示闪烁光标
    state.status = AIGenerationStatus.Completed;
    // 注意：我们保留思考内容，不会清除，这样在完成状态下仍可查看
    
    // 更新界面，先更新状态
    if (editor.view) {
      const statusTr = editor.state.tr;
      editor.view.dispatch(statusTr.setMeta(aiThinkingPluginKey, { 
        generationState: state,
        tempHtml: '',
        updateType: 'status' // 只更新状态
      }));
      
      // 然后更新内容
      const htmlContent = self.storage.mdProcessor.contentToHtml(state.content);
      const contentTr = editor.state.tr;
      editor.view.dispatch(contentTr.setMeta(aiThinkingPluginKey, { 
        generationState: state,
        tempHtml: htmlContent,
        updateType: 'content' // 只更新内容
      }));
    }
  }
};

// 确认生成
const confirmGeneration = (self: { storage: AITextStorage }, editor: Editor) => {
  if (!self.storage.isGenerating || !self.storage.generationState) return false;

  const state = self.storage.generationState;
  // 设置状态为完成
  state.status = AIGenerationStatus.Completed;

  // 停止生成
  if (self.storage.abortController) {
    self.storage.abortController.abort();
    self.storage.abortController = null;
  }

  try {
    // 获取最终的HTML内容
    const finalContent = state.content;
    const htmlContent = self.storage.mdProcessor.contentToHtml(finalContent);
    
    // 清除临时装饰器
    editor.view.dispatch(editor.state.tr.setMeta('clearAIDecoration', true));
    
    // 找到光标所在节点
    const { doc } = editor.state;
    const $pos = doc.resolve(state.startPos);

    // 获取当前节点的深度和位置信息
    const currentDepth = $pos.depth;
    const currentNodeEnd = currentDepth > 0 ? $pos.after(currentDepth) : doc.content.size;
    
    // 插入内容
    editor.commands.insertContentAt(currentNodeEnd, htmlContent);
    
    // 设置光标到文档末尾
    setTimeout(() => {
      if (editor.view) {
        const docSize = editor.state.doc.content.size;
        editor.commands.setTextSelection(docSize);
      }
    }, 0);
    
    // 重置生成状态
    self.storage.isGenerating = false;
    self.storage.generationState = null;
    self.storage.mdProcessor.reset();
    
    return true;
  } catch (error) {
    console.error('确认生成时出错:', error);
    
    // 出错时设置错误状态
    if (self.storage.generationState) {
      self.storage.generationState.status = AIGenerationStatus.Error;
      self.storage.generationState.errorMessage = "确认生成时出错";
      
      // 更新装饰器以显示错误状态
      if (editor.view) {
        editor.view.dispatch(
          editor.state.tr.setMeta(aiThinkingPluginKey, { 
            generationState: self.storage.generationState,
            updateType: 'status' // 添加状态更新标记
          })
        );
      }
      
      // 2秒后清除错误显示
      setTimeout(() => {
        // 清除生成状态
        self.storage.isGenerating = false;
        self.storage.generationState = null;
        self.storage.mdProcessor.reset();
        
        // 清除装饰器
        if (editor.view) {
          editor.view.dispatch(
            editor.state.tr.setMeta('clearAIDecoration', true)
          );
        }
      }, 2000);
    } else {
      // 如果没有生成状态，直接清除
      self.storage.isGenerating = false;
      self.storage.generationState = null;
      self.storage.mdProcessor.reset();
    }
    
    return false;
  }
};

// 取消生成
const cancelGeneration = (self: { storage: AITextStorage }, editor: Editor) => {
  if (!self.storage.isGenerating || !self.storage.generationState) return false;

  // 停止生成
  if (self.storage.abortController) {
    self.storage.abortController.abort();
    self.storage.abortController = null;
  }

  try {
    // 清除装饰器
    editor.view.dispatch(editor.state.tr.setMeta('clearAIDecoration', true));
    
    // 将光标放回原位置
    const tr = editor.state.tr;
    tr.setSelection(TextSelection.create(tr.doc, self.storage.generationState.startPos));
    editor.view.dispatch(tr);
    
    // 重置生成状态
    self.storage.isGenerating = false;
    self.storage.generationState = null;
    self.storage.mdProcessor.reset();
    
    return true;
  } catch (error) {
    console.error('取消生成时出错:', error);
    
    // 出错时也清除状态
    self.storage.isGenerating = false;
    self.storage.generationState = null;
    self.storage.mdProcessor.reset();
    
    return false;
  }
};

// AI装饰器管理器类
class AIDecorationManager {
  // 容器ID
  readonly id: string;
  
  // 容器DOM元素引用
  private containerElement: HTMLElement | null = null;
  
  // 子元素引用
  private statusElement: HTMLElement | null = null;
  private thinkingContainerElement: HTMLElement | null = null;
  private thinkingContentElement: HTMLElement | null = null;
  private contentElement: HTMLElement | null = null;
  
  // 思考内容可见状态
  private isThinkingVisible: boolean = false;
  
  constructor(id: string) {
    this.id = id;
  }
  
  // 获取容器装饰器
  getContainerDecoration(pos: number): Decoration {
    return Decoration.widget(pos, () => {
      // 创建容器元素
      this.containerElement = document.createElement('div');
      this.containerElement.classList.add('ai-generation-container');
      this.containerElement.setAttribute('data-decor-id', this.id);
      
      // 创建子容器
      this.statusElement = this.createStatusContainer();
      this.thinkingContainerElement = this.createThinkingContainer();
      this.contentElement = this.createContentContainer();
      
      // 添加子容器到主容器
      this.containerElement.appendChild(this.statusElement);
      this.containerElement.appendChild(this.thinkingContainerElement);
      this.containerElement.appendChild(this.contentElement);
      
      return this.containerElement;
    }, { 
      side: 1,
      stopEvent: (event) => {
        // 阻止点击事件传播，以防止光标位置改变
        // 但允许滚动等其他事件
        if (event.type === 'mousedown' || event.type === 'click') {
          const target = event.target as HTMLElement;
          // 如果点击的是内容区域，允许选择文本
          if (target.closest(`.ai-content-container[data-decor-id="${this.id}-content"]`)) {
            return false;
          }
          return true;
        }
        return false;
      }
    });
  }
  
  // 创建状态容器
  private createStatusContainer(): HTMLElement {
    const element = document.createElement('div');
    element.classList.add('ai-status-container');
    element.setAttribute('data-decor-id', `${this.id}-status`);
    return element;
  }
  
  // 创建思考容器
  private createThinkingContainer(): HTMLElement {
    const element = document.createElement('div');
    element.classList.add('ai-thinking-container');
    element.setAttribute('data-decor-id', `${this.id}-thinking`);
    
    // 如果之前是可见的，恢复可见状态
    if (this.isThinkingVisible) {
      element.classList.add('visible');
    }
    
    // 创建思考内容元素
    this.thinkingContentElement = document.createElement('div');
    this.thinkingContentElement.classList.add('ai-thinking-content');
    this.thinkingContentElement.setAttribute('data-decor-id', `${this.id}-thinking-content`);
    
    element.appendChild(this.thinkingContentElement);
    return element;
  }
  
  // 创建内容容器
  private createContentContainer(): HTMLElement {
    const element = document.createElement('div');
    element.classList.add('ai-content-container');
    element.setAttribute('data-decor-id', `${this.id}-content`);
    return element;
  }
  
  // 更新状态指示器
  updateStatus(state: AIGenerationState): void {
    if (!this.statusElement) return;
    
    // 清空当前内容
    this.statusElement.innerHTML = '';
    
    // 创建状态指示器
    const indicator = document.createElement('span');
    
    // 根据状态设置样式和内容
    switch (state.status) {
      case AIGenerationStatus.Thinking:
      case AIGenerationStatus.Started:
        indicator.classList.add('ai-thinking-indicator');
        indicator.setAttribute('data-ai-state', 'thinking');
        indicator.innerHTML = '<span>思考中</span><span class="thinking-dots">...</span>';
        break;
      case AIGenerationStatus.Error:
        indicator.classList.add('ai-error-indicator');
        indicator.innerHTML = '<span>错误</span>';
        if (state.errorMessage) {
          indicator.setAttribute('title', state.errorMessage);
        }
        break;
      case AIGenerationStatus.Generating:
        indicator.classList.add('ai-thinking-indicator');
        indicator.setAttribute('data-ai-state', 'generating');
        if (state.thinkingContent) {
          indicator.innerHTML = '<span>思考完毕。生成中</span><span class="thinking-dots">...</span>';
        } else {
          indicator.innerHTML = '<span>生成中</span><span class="thinking-dots">...</span>';
        }
        break;
      case AIGenerationStatus.Completed:
        indicator.classList.add('ai-thinking-indicator');
        indicator.setAttribute('data-ai-state', 'completed');
        indicator.innerHTML = '<span>生成完毕</span>';
        break;
    }
    
    // 始终添加点击行为，无论是否有思考内容
    indicator.classList.add('clickable');
    
    // 根据是否有思考内容设置不同的提示
    if (state.thinkingContent) {
      indicator.setAttribute('title', '点击查看/隐藏思考过程');
      indicator.onclick = this.toggleThinkingVisibility.bind(this);
    } else if (state.status === AIGenerationStatus.Thinking) {
      // 如果正在思考但还没有内容
      indicator.setAttribute('title', '思考中，内容将很快显示');
      indicator.onclick = this.toggleThinkingVisibility.bind(this);
    } else {
      // 如果没有思考内容
      indicator.setAttribute('title', '无思考内容可显示');
      // 仍然添加点击事件，即使没有内容也显示空白思考区域
      indicator.onclick = this.toggleThinkingVisibility.bind(this);
    }
    
    // 添加到状态容器
    this.statusElement.appendChild(indicator);
  }
  
  // 更新思考内容
  updateThinking(state: AIGenerationState): void {
    if (!this.thinkingContentElement) return;
    
    if (state.thinkingContent) {
      // 处理思考内容的Markdown格式
      const thinkingHtml = markdownProcessor.contentToHtml(state.thinkingContent);
      this.thinkingContentElement.innerHTML = `<div class="thinking-title">思考过程</div><div class="thinking-body">${thinkingHtml}</div>`;
    } else if (state.status === AIGenerationStatus.Thinking) {
      // 如果正在思考但还没有内容
      this.thinkingContentElement.innerHTML = `<div class="thinking-title">思考过程</div><div class="thinking-body"><p>思考中...</p></div>`;
    } else {
      // 如果没有思考内容
      this.thinkingContentElement.innerHTML = `<div class="thinking-title">思考过程</div><div class="thinking-body"><p>无思考内容</p></div>`;
    }
  }
  
  // 更新生成内容
  updateContent(state: AIGenerationState, html: string): void {
    if (!this.contentElement) return;
    
    // 更新内容
    this.contentElement.innerHTML = html;
    
    // 如果正在生成中，添加闪烁光标
    if (state.status === AIGenerationStatus.Generating) {
      const cursorElement = document.createElement('span');
      cursorElement.classList.add('ai-fake-cursor');
      this.contentElement.appendChild(cursorElement);
    }
  }
  
  // 切换思考内容可见性
  toggleThinkingVisibility(e: MouseEvent): void {
    e.preventDefault();
    e.stopPropagation();
    
    if (this.thinkingContainerElement) {
      this.isThinkingVisible = !this.isThinkingVisible;
      this.thinkingContainerElement.classList.toggle('visible');
    }
  }
}

// 创建装饰器插件
const createDecorationPlugin = () => {
  // 装饰器管理器实例
  let decorManager: AIDecorationManager | null = null;
  
  return new Plugin({
    key: aiThinkingPluginKey,
    
    state: {
      init() {
        return DecorationSet.empty;
      },
      
      apply(tr, decorationSet) {
        const metadata = tr.getMeta(aiThinkingPluginKey);
        if (metadata) {
          const { generationState, tempHtml, updateType } = metadata;
          
          if (generationState) {
            // 如果是初始化或位置变更，创建新的管理器和装饰器
            if (updateType === 'init' || updateType === 'position' || !decorManager) {
              decorManager = new AIDecorationManager(`ai-gen-${Date.now()}`);
              const containerDecoration = decorManager.getContainerDecoration(generationState.startPos);
              
              // 生成新的装饰器集合
              const newDecorationSet = DecorationSet.create(tr.doc, [containerDecoration]);
              
              // 初始更新所有内容
              decorManager.updateStatus(generationState);
              if (generationState.thinkingContent) {
                decorManager.updateThinking(generationState);
              }
              if (tempHtml) {
                decorManager.updateContent(generationState, tempHtml);
              }
              
              return newDecorationSet;
            }
            
            // 根据更新类型选择性地更新
            if (updateType === 'status' || !updateType) {
              decorManager.updateStatus(generationState);
            }
            
            if ((updateType === 'thinking' || !updateType) && generationState.thinkingContent) {
              decorManager.updateThinking(generationState);
            }
            
            if ((updateType === 'content' || !updateType) && tempHtml) {
              decorManager.updateContent(generationState, tempHtml);
            }
            
            // 保持现有装饰器集合不变
            return decorationSet;
          }
        }
        
        // 如果需要清除
        if (tr.getMeta('clearAIDecoration')) {
          decorManager = null;
          return DecorationSet.empty;
        }
        
        // 处理文档变化
        if (tr.docChanged) {
          return decorationSet.map(tr.mapping, tr.doc);
        }
        
        return decorationSet;
      }
    },
    
    props: {
      decorations(state) {
        return this.getState(state);
      }
    }
  });
};

// 默认提示词配置
const DEFAULT_PROMPTS = {
  prompt: '根据用户输入的上下文，生成Markdown。',
  systemPrompt: '你是一个有帮助的AI编辑助手。你生成的回复应该是有帮助的、富有创意的和Markdown格式的。可以使用表格、任务列表等GitHub风格Markdown特性。添加<think>在思考过程的开始，使用</think>标记思考结束，然后提供最终的Markdown格式的回答。思考过程不会被用户看见，只用于帮助你组织思路。'
};

// 获取编辑器上下文内容
const getEditorContext = (editor: Editor, cursorPos: number): string => {
  const doc = editor.state.doc;
  
  // 如果文档为空，返回空字符串
  if (doc.childCount === 0) {
    return '';
  }
  
  // 找到光标所在的节点及其位置
  const $pos = doc.resolve(cursorPos);
  const currentNode = $pos.parent;
  
  // 如果光标在文本块内，直接返回该文本块的内容
  if (currentNode.isTextblock) {
    const from = $pos.start();
    const to = $pos.end();
    return doc.textBetween(from, to, ' ');
  }
  
  // 如果光标不在文本块内，尝试获取最近的前一个文本块
  const pos = $pos.before();
  if (pos > 0) {
    const $prevPos = doc.resolve(pos);
    const prevNode = $prevPos.parent;
    
    if (prevNode.isTextblock) {
      const from = $prevPos.start();
      const to = $prevPos.end();
      return doc.textBetween(from, to, ' ');
    }
  }
  
  // 如果找不到相关文本块，返回空字符串
  return '';
};

// 处理生成更新
const handleGenerationUpdate = (
  self: { storage: AITextStorage }, 
  editor: Editor, 
  chunk: string, 
  isThinking: boolean = false
) => {
  // 如果不存在生成状态，或者编辑器视图不存在，直接返回
  if (!self.storage.generationState || !editor.view) return;

  // 获取当前生成状态
  const generationState = self.storage.generationState;

  try {
    // 如果是思考阶段
    if (isThinking) {
      // 累积思考内容
      if (!generationState.thinkingContent) {
        generationState.thinkingContent = chunk;
      } else {
        generationState.thinkingContent += chunk;
      }
      
      // 更新状态
      let statusChanged = false;
      if (generationState.status !== AIGenerationStatus.Thinking) {
        generationState.status = AIGenerationStatus.Thinking;
        statusChanged = true;
      }
      
      // 如果状态变更，先更新状态
      if (statusChanged) {
        const statusTr = editor.state.tr;
        editor.view.dispatch(statusTr.setMeta(aiThinkingPluginKey, { 
          generationState, 
          tempHtml: '',
          updateType: 'status'
        }));
      }
      
      // 每个token都更新思考内容，确保实时显示
      const thinkingTr = editor.state.tr;
      editor.view.dispatch(thinkingTr.setMeta(aiThinkingPluginKey, { 
        generationState, 
        tempHtml: '',
        updateType: 'thinking' // 使用思考内容更新类型
      }));
    } 
    // 如果是内容生成阶段
    else {
      // 如果之前是思考阶段，现在是第一个内容块，更新状态
      let statusChanged = false;
      if (generationState.status === AIGenerationStatus.Thinking || 
          generationState.status === AIGenerationStatus.Started) {
        generationState.status = AIGenerationStatus.Generating;
        statusChanged = true;
      }

      // 累积生成内容
      if (!generationState.tempContent) {
        generationState.tempContent = chunk;
      } else {
        generationState.tempContent += chunk;
      }

      // 如果状态有变更，先更新状态
      if (statusChanged) {
        const statusTr = editor.state.tr;
        editor.view.dispatch(statusTr.setMeta(aiThinkingPluginKey, { 
          generationState, 
          tempHtml: '',
          updateType: 'status'
        }));
      }
      
      // 处理Markdown并更新装饰器内容
      const tempHtml = markdownProcessor.contentToHtml(generationState.tempContent);
      const contentTr = editor.state.tr;
      editor.view.dispatch(contentTr.setMeta(aiThinkingPluginKey, { 
        generationState, 
        tempHtml,
        updateType: 'content' // 使用内容更新类型
      }));
    }
  } catch (error: unknown) {
    console.error('处理生成更新时出错:', error);
    
    // 发生错误时设置错误状态
    generationState.status = AIGenerationStatus.Error;
    generationState.errorMessage = `处理更新时出错: ${error instanceof Error ? error.message : String(error)}`;
    
    // 更新装饰器，显示错误状态
    const tr = editor.state.tr;
    editor.view.dispatch(tr.setMeta(aiThinkingPluginKey, { 
      generationState, 
      tempHtml: '',
      updateType: 'status' // 错误是状态变更
    }));
  }
};

// AI文本扩展实现
export const AIText = Extension.create({
  name: 'aiText',

  addStorage() {
    return {
      isGenerating: false,
      abortController: null,
      generationState: null,
      isAIUpdating: false,
      accumulatedText: '',
      isInThinkingPhase: false,
      mdProcessor: markdownProcessor
    } as AITextStorage;
  },

  addCommands() {
    return {
      generateText: () => ({ editor }) => {
        // 如果已经在生成中，不做任何事情
        if (this.storage.isGenerating) {
          return false;
        }
        
        // 获取光标位置
        const { from } = editor.state.selection;
        
        // 创建终止控制器
        this.storage.abortController = new AbortController();
        
        // 初始化生成状态
        this.storage.isGenerating = true;
        this.storage.accumulatedText = '';
        this.storage.isInThinkingPhase = false;
        
        // 重置增量处理器
        this.storage.mdProcessor.reset();
        
        // 设置生成状态
        this.storage.generationState = {
          status: AIGenerationStatus.Started,
          thinking: false,
          content: '',
          startPos: from,
          endPos: from,
          endMarkerPos: from,
          showAIPrefix: false,
          tempContent: '',
          errorMessage: undefined,
          thinkingContent: undefined
        };
        
        // 更新装饰器
        if (editor.view) {
          editor.view.dispatch(
            editor.state.tr.setMeta(aiThinkingPluginKey, { 
              generationState: this.storage.generationState,
              updateType: 'init' // 添加初始化类型标记
            })
          );
        }
        
        // 定义回调函数，处理每个更新
        const handleUpdate = async (token: string) => {
          await handleTextUpdate(this, editor, token);
        };
        
        // 获取AI提供商和配置
        const provider = userSettings.getAIProvider();
        const config = provider ? userSettings.getAIConfig(provider) : {};
        
        // 获取上下文内容
        const context = getEditorContext(editor, from);
        
        // 将上下文添加到提示词中
        const enrichedPrompt = context 
          ? `以下是当前编辑器中的内容：\n\n${context}\n\n请基于上述内容，生成连贯的后续文本。保持与上下文的一致性，确保风格和主题的延续。生成内容应该是Markdown格式的，可以使用表格、任务列表等GitHub风格Markdown特性。`
          : DEFAULT_PROMPTS.prompt;
        
        // 获取AI服务
        const service = AIServiceFactory.getService(provider, config || {});
        
        if (!service) {
          console.error('未配置AI提供商或提供商不可用');
          return false;
        }
        
        // 使用AI服务生成文本
        service.generateText({
          prompt: enrichedPrompt,
          systemPrompt: DEFAULT_PROMPTS.systemPrompt,
          temperature: (config?.temperature as number) ?? 0.7,
          maxTokens: (config?.maxTokens as number) ?? 1000,
          topP: (config?.topP as number) ?? 1,
          onUpdate: handleUpdate,
          abortSignal: this.storage.abortController.signal
        }).catch((error: Error) => {
          console.error('AI文本生成出错:', error);
          
          // 设置错误状态
          if (this.storage.generationState) {
            this.storage.generationState.status = AIGenerationStatus.Error;
            this.storage.generationState.errorMessage = error?.message || "生成过程中出现错误";
            
            // 更新装饰器以显示错误状态
            if (editor.view) {
              editor.view.dispatch(
                editor.state.tr.setMeta(aiThinkingPluginKey, { 
                  generationState: this.storage.generationState,
                  updateType: 'status' // 添加状态更新标记
                })
              );
            }
            
            // 2秒后清除错误显示
            setTimeout(() => {
              // 清除装饰器
              if (editor.view) {
                editor.view.dispatch(
                  editor.state.tr.setMeta('clearAIDecoration', true)
                );
              }
              
              // 清除生成状态
              this.storage.isGenerating = false;
              this.storage.generationState = null;
              this.storage.abortController = null;
            }, 2000);
          } else {
            // 如果没有生成状态，直接清除
            this.storage.isGenerating = false;
            this.storage.abortController = null;
            
            // 清除装饰器
            if (editor.view) {
              editor.view.dispatch(
                editor.state.tr.setMeta('clearAIDecoration', true)
              );
            }
          }
        });
        
        return true;
      },
      
      confirmAIText: () => ({ editor }) => {
        return confirmGeneration(this, editor);
      },
      
      cancelAIText: () => ({ editor }) => {
        return cancelGeneration(this, editor);
      }
    };
  },

  addProseMirrorPlugins() {
    return [
      createDecorationPlugin()
    ];
  },

  addKeyboardShortcuts() {
    return {
      'Escape': () => {
        if (this.storage.isGenerating) {
          return this.editor.commands.cancelAIText();
        }
        return false;
      },
      'Mod-Enter': () => {
        if (this.storage.isGenerating) {
          return this.editor.commands.confirmAIText();
        }
        return this.editor.commands.generateText();
      },
      // 空格键和回车键也可以确认生成
      'Space': () => {
        if (this.storage.isGenerating) {
          return this.editor.commands.confirmAIText();
        }
        return false;
      },
      'Enter': () => {
        if (this.storage.isGenerating) {
          return this.editor.commands.confirmAIText();
        }
        return false;
      }
    };
  },

  onUpdate: function() {
    // 使用箭头函数保持 this 上下文
    return ({ editor, transaction }: { editor: Editor; transaction: Transaction }) => {
      // 如果不是正在生成中，或者是编辑操作，则忽略
      if (!this.storage.isGenerating || transaction.docChanged) {
        return;
      }

      try {
        // 检查是否有响应数据
        const meta = transaction.getMeta('aiResponse') as AiResponseMeta | null;
        if (!meta) return;

        // 如果有响应数据，处理更新
        const { content, isThinking } = meta;
        handleGenerationUpdate(this, editor, content, isThinking);

      } catch (error) {
        console.error('处理AI响应更新时出错:', error);
      }
    };
  }
});